import { FC, useMemo, useState } from 'react'
import { useArpMedias } from '../../../queries/arpMedia'
import Button from '../../core/Button/Button'
import { FieldErrorLabel } from '../../core/FieldErrorLabel'
import { FieldLabel } from '../../core/FieldLabel'
import { Flex } from '../../core/Flex'
import Text from '../../core/Text/Text'
import { TextArea } from '../../core/TextArea'
import { Badge } from '../components/common'

const useArpIdsInput = ({ onChange }: { onChange: (ids: number[]) => void }) => {
  const [textValue, setTextValue] = useState('')
  const [parsedArpIds, setParsedArpIds] = useState<number[]>([])
  const arpMediaQuery = useArpMedias({ ids: parsedArpIds }, { enabled: !!parsedArpIds.length })

  const handleTextInputChange = (value: string) => {
    setTextValue(value)
    const splits = value.split(',')

    const ids: number[] = []
    splits.forEach((split) => {
      const parsed = parseInt(split.replaceAll(/\s/g, ''))

      if (!isNaN(parsed)) {
        ids.push(parsed)
      }
    })

    setParsedArpIds(ids)
    onChange(ids)
  }

  const detectedArpMedia = useMemo(
    () => arpMediaQuery.data?.pages.flatMap((page) => page.data.map((arpMedia) => arpMedia)) || [],
    [arpMediaQuery.data]
  )

  return { textValue, parsedArpIds, detectedArpMedia, handleTextInputChange }
}

interface Props {
  onSubmit: (ids: number[]) => Promise<void>
  onChange: (ids: number[]) => void
  isDisabled?: boolean
}

export const ArpIdsInput: FC<Props> = ({ onSubmit, isDisabled, onChange }) => {
  const { textValue, parsedArpIds, detectedArpMedia, handleTextInputChange } = useArpIdsInput({
    onChange
  })

  return (
    <Flex direction="column" css={{ maxWidth: '400px' }}>
      <Flex css={{ gap: '8px', alignItems: 'baseline' }}>
        <FieldLabel htmlFor="description">Add Arp IDs</FieldLabel>
        <Text type="secondary">(comma separated IDs)</Text>
      </Flex>
      <TextArea
        value={textValue}
        onChange={(e) => handleTextInputChange(e.target.value)}
        disabled={isDisabled}
        placeholder="1, 2, 3, ..."
      />
      {textValue && !parsedArpIds.length && (
        <FieldErrorLabel css={{ marginTop: '8px' }}>No Arp Ids detected</FieldErrorLabel>
      )}
      <>
        <Text type="secondary" css={{ marginTop: '8px', marginBottom: '8px' }}>
          Detected Arp Media:
        </Text>
        {detectedArpMedia.length === 0 && <Text css={{ marginBottom: '8px' }}>None</Text>}
        {detectedArpMedia.map((arpMedia) => (
          <Flex css={{ gap: '8px', alignItems: 'center', paddingTop: '4px', paddingBottom: '4px' }}>
            <Text>{arpMedia.id}</Text>
            <Text>{arpMedia.metadata.title}</Text>
            <Badge css={{ backgroundColor: '$background__secondary' }}>{arpMedia.type}</Badge>
          </Flex>
        ))}
        {onSubmit && (
          <Button
            big
            type="dark"
            css={{ width: '100%', marginTop: '8px' }}
            disabled={isDisabled || detectedArpMedia.length === 0}
            onClick={async (e) => {
              e.preventDefault()
              await onSubmit(detectedArpMedia.map((x) => x.id))
              handleTextInputChange('')
            }}
          >
            Add Arp Media
          </Button>
        )}
      </>
    </Flex>
  )
}
